<template>
	<div class="page-wrapper">
		<div class="page-card">
			<div class="category-header">
				<span class="category-icon">📊</span>
				<h1>经济管理</h1>
			</div>
			<p class="category-desc">每个项目包含万字说明文档</p>

			<!-- 搜索栏 -->
			<div class="search-bar">
				<div class="search-input-wrapper">
					<span class="search-icon">🔍</span>
					<input type="text" v-model="searchKeyword" placeholder="按作品名称搜索..." class="search-input"
						@input="handleSearch" />
					<button v-if="searchKeyword" class="clear-btn" @click="clearSearch" title="清空搜索">
						✕
					</button>
				</div>
				<div class="search-stats" v-if="searchKeyword">
					找到 <strong>{{ totalRecords }}</strong> 个相关作品
				</div>
			</div>

			<!-- 固定高度的作品容器 -->
			<div class="works-container">
				<!-- 加载状态 -->
				<div class="loading-state" v-if="loading">
					<div class="loading-spinner"></div>
					<p>加载中...</p>
				</div>

				<!-- 作品网格 -->
				<div class="works-grid" v-else-if="paginatedWorks.length > 0">
					<div class="work-card" v-for="work in paginatedWorks" :key="work.id" @click="goToWorkDetail(work)">
						<div class="work-icon">{{ work.emoji }}</div>
						<h3>{{ work.name }}</h3>
						<p>{{ work.desc }}</p>
						<div class="card-link-hint">点击查看详情 →</div>
					</div>
				</div>

				<!-- 无搜索结果提示 -->
				<div class="no-result" v-else>
					<span class="no-result-icon">🔍</span>
					<p>没有找到与“<strong>{{ searchKeyword }}</strong>”相关的作品</p>
					<button class="reset-btn" @click="clearSearch">查看全部作品</button>
				</div>
			</div>

			<!-- 分页工具 -->
			<div class="pagination-container" v-if="totalPages > 0 && !loading">
				<div class="pagination-left">
					<span class="page-size-label">每页显示：</span>
					<select v-model="pageSize" class="page-size-select" @change="handlePageSizeChange">
						<option :value="12">12条</option>
						<option :value="18">18条</option>
						<option :value="24">24条</option>
						<option :value="30">30条</option>
						<option :value="36">36条</option>
						<option :value="48">48条</option>
						<option :value="60">60条</option>
					</select>
					<span class="total-count">共 {{ totalRecords }} 条记录</span>
				</div>

				<div class="pagination" v-if="totalPages > 1">
					<button class="page-btn" :disabled="currentPage === 1" @click="goToPage(1)" title="首页">
						&laquo;
					</button>
					<button class="page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)"
						title="上一页">
						&lsaquo;
					</button>
					<div class="page-numbers">
						<button v-for="page in displayedPages" :key="page" class="page-num"
							:class="{ active: currentPage === page }" @click="goToPage(page)"
							:disabled="page === '...'">
							{{ page }}
						</button>
					</div>
					<button class="page-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)"
						title="下一页">
						&rsaquo;
					</button>
					<button class="page-btn" :disabled="currentPage === totalPages" @click="goToPage(totalPages)"
						title="末页">
						&raquo;
					</button>
				</div>

				<div class="pagination-right" v-if="totalPages > 1">
					<span class="page-range">第 {{ currentPage }} / {{ totalPages }} 页</span>
					<div class="goto-page">
						<span>跳至</span>
						<input type="number" v-model.number="gotoPageNum" class="goto-input" @keyup.enter="gotoPage"
							min="1" :max="totalPages" />
						<span>页</span>
						<button class="goto-btn" @click="gotoPage">GO</button>
					</div>
				</div>
			</div>

			<div class="stats-bar">
				<div class="badge">计算机程序 · 共 {{ allWorksCount }} 个作品</div>
				<div class="display-range" v-if="paginatedWorks.length > 0 && !loading">
					当前显示 {{ (currentPage - 1) * pageSize + 1 }} -
					{{ Math.min(currentPage * pageSize, totalRecords) }} 条
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import {
		management
	} from '../dataView/management.js';

	export default {
		name: 'DataVizView',
		data() {
			return {
				currentPage: 1,
				pageSize: 30,
				gotoPageNum: 1,
				searchKeyword: '',
				loading: false,
				allWorks: [],
				iconLibrary: [
					'🏙️', '📈', '🔥', '🖥️', '🗺️', '🔍', '🌤️', '💰', '🚦', '📱',
					'🏥', '📚', '🚚', '⚡', '🌾', '⚽', '🔒', '👥', '⛓️', '🏠',
					'🎮', '🏭', '🛒', '📊', '🌍', '✈️', '🍽️', '🔬', '🎬', '🏛️',
					'🎨', '🤖', '💡', '🔔', '📧', '💎', '🌟', '⚙️', '📦', '🔄',
					'📡', '🎯', '💼', '🔧', '📝', '💊', '🧪', '🎓', '🏆', '🎵',
					'📷', '💻', '📱', '⌚', '🎥', '📹', '🔊', '🎚️', '🎛️', '📟'
				]
			};
		},
		computed: {
			allWorksCount() {
				return this.allWorks.length;
			},
			filteredWorks() {
				if (!this.searchKeyword.trim()) {
					return this.allWorks;
				}
				const keyword = this.searchKeyword.trim().toLowerCase();
				return this.allWorks.filter(work =>
					work.name.toLowerCase().includes(keyword)
				);
			},
			totalRecords() {
				return this.filteredWorks.length;
			},
			totalPages() {
				return Math.ceil(this.totalRecords / this.pageSize);
			},
			paginatedWorks() {
				const start = (this.currentPage - 1) * this.pageSize;
				const end = start + this.pageSize;
				return this.filteredWorks.slice(start, end);
			},
			displayedPages() {
				const total = this.totalPages;
				const current = this.currentPage;
				const delta = 2;
				const range = [];

				for (let i = 1; i <= total; i++) {
					if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
						range.push(i);
					} else if (range[range.length - 1] !== '...') {
						range.push('...');
					}
				}
				return range;
			}
		},
		watch: {
			searchKeyword(newVal, oldVal) {
				if (newVal !== oldVal) {
					this.currentPage = 1;
					this.gotoPageNum = 1;
				}
			},
			currentPage(newVal) {
				this.gotoPageNum = newVal;
			},
			pageSize() {
				const maxPage = Math.ceil(this.totalRecords / this.pageSize);
				if (this.currentPage > maxPage && maxPage > 0) {
					this.currentPage = maxPage;
					this.gotoPageNum = this.currentPage;
				}
			}
		},
		methods: {
			getRandomIcon() {
				const randomIndex = Math.floor(Math.random() * this.iconLibrary.length);
				return this.iconLibrary[randomIndex];
			},
			async fetchWorksData() {
				this.loading = true;
				await new Promise(resolve => setTimeout(resolve, 300));

				const worksData = [];
				const baseWorks = management;
				const dataCount = management.length;

				for (let i = 0; i < dataCount; i++) {
					const baseWork = baseWorks[i % baseWorks.length];
					const version = Math.floor(i / baseWorks.length) + 1;

					worksData.push({
						id: i + 1,
						name: version > 1 ? `${baseWork.name} v${version}` : baseWork.name,
						desc: `${baseWork.desc}${version > 1 ? ` (第${version}版)` : ''}`,
						path: baseWork.path,
						external: true,
						emoji: this.getRandomIcon()
					});
				}

				this.allWorks = worksData;
				this.loading = false;
			},
			handleSearch() {
				if (this.searchTimer) {
					clearTimeout(this.searchTimer);
				}
				this.searchTimer = setTimeout(() => {}, 300);
			},
			goToPage(page) {
				if (page >= 1 && page <= this.totalPages) {
					this.currentPage = page;
					window.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				}
			},
			gotoPage() {
				let page = this.gotoPageNum;
				if (isNaN(page)) page = this.currentPage;
				page = Math.max(1, Math.min(page, this.totalPages));
				this.currentPage = page;
				this.gotoPageNum = page;
			},
			handlePageSizeChange() {
				const maxPage = Math.ceil(this.totalRecords / this.pageSize);
				if (this.currentPage > maxPage) {
					this.currentPage = maxPage || 1;
					this.gotoPageNum = this.currentPage;
				}
			},
			clearSearch() {
				this.searchKeyword = '';
			},
			goToWorkDetail(work) {
				console.log(`跳转到作品：${work.name}`, work);
				if (work.path) {
					window.open(work.path, '_blank');
				} else {
					alert('该作品的演示链接暂不可用');
				}
			},
			async refreshData() {
				await this.fetchWorksData();
				this.currentPage = 1;
				this.gotoPageNum = 1;
				this.searchKeyword = '';
			}
		},
		async mounted() {
			await this.fetchWorksData();
			console.log(`数据加载完成，共 ${this.allWorks.length} 条记录`);
		},
		beforeDestroy() {
			if (this.searchTimer) {
				clearTimeout(this.searchTimer);
			}
		}
	};
</script>

<style scoped>
	.page-wrapper {
		min-height: 100vh;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		padding: 2rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.page-card {
		background: white;
		border-radius: 2rem;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
		padding: 2rem;
		max-width: 1800px;
		width: 100%;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		transition: all 0.3s ease;
	}

	.category-header {
		display: flex;
		align-items: center;
		gap: 1rem;
		margin-bottom: 1rem;
	}

	.category-icon {
		font-size: 2.5rem;
	}

	h1 {
		font-size: 2rem;
		font-weight: 700;
		color: #0c4a6e;
		margin: 0;
	}

	.category-desc {
		color: #64748b;
		margin-bottom: 2rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid #e2e8f0;
	}

	.works-container {
		flex: 1;
		min-height: 550px;
		display: inline-grid;
		flex-direction: column;
		justify-content: center;
		background: #ffffff;
		border-radius: 1rem;
	}

	.search-bar {
		margin-bottom: 1.5rem;
	}

	.search-input-wrapper {
		position: relative;
		display: flex;
		align-items: center;
	}

	.search-icon {
		position: absolute;
		left: 1rem;
		font-size: 1rem;
		color: #94a3b8;
		pointer-events: none;
	}

	.search-input {
		width: 100%;
		padding: 0.75rem 2.5rem 0.75rem 2.5rem;
		border: 1px solid #e2e8f0;
		border-radius: 0.75rem;
		font-size: 0.95rem;
		transition: all 0.2s;
		background: #f8fafc;
	}

	.search-input:focus {
		outline: none;
		border-color: #0c4a6e;
		background: white;
		box-shadow: 0 0 0 3px rgba(12, 74, 110, 0.1);
	}

	.clear-btn {
		position: absolute;
		right: 0.75rem;
		background: #e2e8f0;
		border: none;
		width: 1.75rem;
		height: 1.75rem;
		border-radius: 50%;
		cursor: pointer;
		font-size: 0.8rem;
		color: #64748b;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.2s;
	}

	.clear-btn:hover {
		background: #cbd5e1;
		color: #334155;
	}

	.search-stats {
		margin-top: 0.5rem;
		font-size: 0.8rem;
		color: #64748b;
	}

	.search-stats strong {
		color: #0c4a6e;
		font-weight: 600;
	}

	.loading-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		text-align: center;
	}

	.loading-spinner {
		width: 50px;
		height: 50px;
		border: 3px solid #e2e8f0;
		border-top-color: #0c4a6e;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
		margin-bottom: 1rem;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	.loading-state p {
		color: #64748b;
		font-size: 0.9rem;
	}

	.works-grid {
		display: flex;
		flex-wrap: wrap;
		/* align-items: center; */
		align-content: flex-start;
		grid-template-columns: repeat(6, 1fr);
		gap: 1.5rem;
		margin: 0;
		/* justify-content: flex-end; */
		width: 100%;
		/* display: grid;
		grid-template-columns: repeat(6, 1fr);
		gap: 1.5rem;
		margin: 0;
		width: 100%; */
	}

	@media (max-width: 1600px) {
		.works-grid {
			grid-template-columns: repeat(5, 1fr);
			gap: 1.25rem;
		}
	}

	@media (max-width: 1400px) {
		.works-grid {
			grid-template-columns: repeat(5, 1fr);
			gap: 1.25rem;
		}
	}

	@media (max-width: 1200px) {
		.works-grid {
			grid-template-columns: repeat(4, 1fr);
			gap: 1.25rem;
		}
	}

	@media (max-width: 900px) {
		.works-grid {
			grid-template-columns: repeat(3, 1fr);
			gap: 1rem;
		}
	}

	@media (max-width: 600px) {
		.works-grid {
			grid-template-columns: repeat(2, 1fr);
			gap: 1rem;
		}
	}

	@media (max-width: 400px) {
		.works-grid {
			grid-template-columns: 1fr;
			gap: 1rem;
		}
	}

	.work-card {
		background: #f8fafc;
		border-radius: 0.875rem;
		padding: 1rem;
		text-align: center;
		transition: all 0.3s;
		border: 1px solid #e2e8f0;
		cursor: pointer;
		position: relative;
		width: 320px;
		/* min-width: 300px; */
		/* max-width: 350px; */
	}

	.work-card::after {
		content: '🔗';
		position: absolute;
		top: 8px;
		right: 8px;
		font-size: 12px;
		opacity: 0.5;
		transition: opacity 0.2s;
	}

	.work-card:hover::after {
		opacity: 1;
	}

	.work-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
		border-color: #0c4a6e;
	}

	.work-card:active {
		transform: translateY(-2px);
	}

	.work-icon {
		font-size: 2rem;
		margin-bottom: 0.5rem;
	}

	.work-card h3 {
		font-size: 0.9rem;
		margin-bottom: 0.4rem;
		color: #1e293b;
		/* white-space: nowrap; */
		white-space: wrap;
		overflow-x: hidden;
		text-overflow: ellipsis;
	}

	.work-card p {
		font-size: 0.7rem;
		color: #64748b;
		white-space: nowrap;
		overflow-x: hidden;
		text-overflow: ellipsis;
		margin-bottom: 0.4rem;
	}

	.card-link-hint {
		font-size: 0.65rem;
		color: #0c4a6e;
		opacity: 0;
		transition: opacity 0.2s;
		margin-top: 0.35rem;
		font-weight: 500;
	}

	.work-card:hover .card-link-hint {
		opacity: 0.8;
	}

	.pagination-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 1rem;
		margin: 2rem 0 1rem;
		padding: 1rem 0;
		border-top: 1px solid #e2e8f0;
		border-bottom: 1px solid #e2e8f0;
	}

	.pagination-left {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.85rem;
		color: #475569;
	}

	.page-size-label {
		white-space: nowrap;
	}

	.page-size-select {
		padding: 0.3rem 0.6rem;
		border: 1px solid #cbd5e1;
		border-radius: 0.5rem;
		background: white;
		cursor: pointer;
		font-size: 0.85rem;
		color: #1e293b;
	}

	.page-size-select:focus {
		outline: none;
		border-color: #0c4a6e;
	}

	.total-count {
		white-space: nowrap;
	}

	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		flex-wrap: wrap;
	}

	.page-btn {
		background: #f1f5f9;
		border: 1px solid #e2e8f0;
		padding: 0.4rem 0.8rem;
		border-radius: 0.5rem;
		cursor: pointer;
		font-size: 0.85rem;
		color: #334155;
		transition: all 0.2s;
		min-width: 2rem;
	}

	.page-btn:hover:not(:disabled) {
		background: #e2e8f0;
		border-color: #cbd5e1;
	}

	.page-btn:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}

	.page-numbers {
		display: flex;
		gap: 0.25rem;
	}

	.page-num {
		min-width: 2.25rem;
		height: 2.25rem;
		display: flex;
		align-items: center;
		justify-content: center;
		background: white;
		border: 1px solid #e2e8f0;
		border-radius: 0.5rem;
		cursor: pointer;
		font-size: 0.85rem;
		color: #334155;
		transition: all 0.2s;
	}

	.page-num:hover:not(:disabled) {
		background: #f1f5f9;
		border-color: #cbd5e1;
	}

	.page-num:disabled {
		cursor: not-allowed;
	}

	.page-num.active {
		background: #0c4a6e;
		border-color: #0c4a6e;
		color: white;
	}

	.pagination-right {
		display: flex;
		align-items: center;
		gap: 1rem;
		font-size: 0.85rem;
		color: #475569;
	}

	.page-range {
		white-space: nowrap;
	}

	.goto-page {
		display: flex;
		align-items: center;
		gap: 0.25rem;
	}

	.goto-input {
		width: 55px;
		padding: 0.3rem 0.4rem;
		border: 1px solid #cbd5e1;
		border-radius: 0.5rem;
		text-align: center;
		font-size: 0.85rem;
	}

	.goto-input:focus {
		outline: none;
		border-color: #0c4a6e;
	}

	.goto-btn {
		background: #0c4a6e;
		color: white;
		border: none;
		padding: 0.3rem 0.8rem;
		border-radius: 0.5rem;
		cursor: pointer;
		font-size: 0.75rem;
		transition: all 0.2s;
	}

	.goto-btn:hover {
		background: #075a8a;
	}

	.stats-bar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-top: 1rem;
	}

	.badge {
		display: inline-block;
		background: #eef2ff;
		color: #1e40af;
		border-radius: 30px;
		padding: 0.3rem 1rem;
		font-size: 0.8rem;
	}

	.display-range {
		font-size: 0.8rem;
		color: #64748b;
	}

	.no-result {
		text-align: center;
		padding: 3rem 2rem;
		background: #f8fafc;
		border-radius: 1rem;
		margin: 1.5rem 0;
	}

	.no-result-icon {
		font-size: 3rem;
		display: block;
		margin-bottom: 1rem;
		opacity: 0.6;
	}

	.no-result p {
		color: #64748b;
		margin-bottom: 1rem;
	}

	.no-result strong {
		color: #0c4a6e;
	}

	.reset-btn {
		background: #0c4a6e;
		color: white;
		border: none;
		padding: 0.5rem 1.5rem;
		border-radius: 2rem;
		cursor: pointer;
		font-size: 0.85rem;
		transition: all 0.2s;
	}

	.reset-btn:hover {
		background: #075a8a;
	}
</style>